import React, { useEffect, useState } from "react";
import { GraduationCap, Plus, Trash2, Mail, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";

export default function TeachersTab() {
  const [teachers, setTeachers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [deviceLimit, setDeviceLimit] = useState("unlimited");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/teachers");
      setTeachers(r.data);
    } catch (err) {
      console.warn("Load teachers failed:", err?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setAdding(true);
    try {
      await api.post("/admin/teachers", {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        device_limit: deviceLimit === "unlimited" ? null : Number(deviceLimit),
      });
      setEmail(""); setName(""); setDeviceLimit("unlimited");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "Couldn't add teacher"));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (user_id, teacherEmail) => {
    if (!window.confirm(`Revoke teacher access for ${teacherEmail}? They'll remain as a student with their progress intact.`)) return;
    try {
      await api.delete(`/admin/teachers/${user_id}`);
      await load();
    } catch (err) {
      console.warn("Revoke teacher failed:", err?.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="teachers-tab">
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap className="w-5 h-5 text-sage" strokeWidth={1.7} />
          <h2 className="font-display text-lg font-semibold">Add a teacher</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Teachers sign in with Google — no code or extra step. They get read-only access to the student roster &amp; analytics plus the regular student content experience. They do <span className="font-medium text-foreground">not</span> manage emails, codes, devices, approvals, or content generation.
        </p>
        <form onSubmit={add} className="grid sm:grid-cols-[1.4fr_1fr_auto_auto] gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teacher@egns-school.de"
            className="border border-border rounded-lg px-3 py-2 text-sm focus:border-sage focus:outline-none"
            data-testid="teacher-email-input"
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="border border-border rounded-lg px-3 py-2 text-sm focus:border-sage focus:outline-none"
            data-testid="teacher-name-input"
          />
          <select
            value={deviceLimit}
            onChange={(e) => setDeviceLimit(e.target.value)}
            data-testid="teacher-device-limit"
            className="border border-border rounded-lg px-3 py-2 text-sm focus:border-sage focus:outline-none bg-white"
          >
            <option value="unlimited">Unlimited devices</option>
            <option value="1">1 device</option>
            <option value="2">2 devices</option>
            <option value="3">3 devices</option>
            <option value="5">5 devices</option>
          </select>
          <button
            type="submit"
            disabled={adding || !email.trim()}
            data-testid="add-teacher-submit"
            className="inline-flex items-center gap-1.5 bg-sage hover:bg-sage-dark disabled:opacity-40 text-white font-medium px-4 py-2 rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> {adding ? "Adding…" : "Add"}
          </button>
        </form>
        {error && (
          <div className="mt-3 p-3 bg-terracotta/10 border border-terracotta/30 rounded-lg text-sm text-terracotta flex items-start gap-2" data-testid="teacher-add-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-sage" />
            <h3 className="font-medium">Current teachers</h3>
          </div>
          <div className="text-xs text-muted-foreground">{teachers.length} active</div>
        </div>
        {teachers.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="teachers-empty">
            No teachers yet. Add the first one above.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="teachers-list">
            {teachers.map((t) => (
              <li
                key={t.user_id}
                className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-bone/30"
                data-testid={`teacher-row-${t.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-sage/10 text-sage flex items-center justify-center font-semibold">
                    {(t.name || t.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.name || t.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.email}</div>
                  </div>
                </div>
                <button
                  onClick={() => remove(t.user_id, t.email)}
                  data-testid={`revoke-teacher-${t.user_id}`}
                  className="text-muted-foreground hover:text-terracotta p-2 rounded-lg hover:bg-terracotta/10"
                  title="Revoke teacher access"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
